/**
 * ProfitLossTable — multi-year Profit & Loss Statement table.
 *
 * Shows income statement line items across up to 10 years.
 */
import { Card, CardHeader, CardBody } from '../../ui/Card';
import {
  formatFiscalYear,
  formatCompactCurrency,
  formatPercent,
  formatUnitLabel,
} from '../utils/formatters';
import { resolveRecordValue } from '../utils/extendedFields';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
}

export default function ProfitLossTable({ data, region }: Props) {
  const years = data;
  const unitLabel = formatUnitLabel(region);

  type RowDef = {
    label: string;
    key: keyof UnifiedFinancialRecord;
    format: 'currency' | 'pct' | 'number';
    indent?: boolean;
    bold?: boolean;
    separator?: boolean;
    /**
     * PDF-only extras have no first-class DTO field — resolve them through
     * every spelling they may arrive under (extended_fields carries the Neo4j
     * PascalCase names). Rows with no value in any year are dropped, so these
     * are harmless for scraped companies where the properties don't exist.
     */
    extKeys?: string[];
  };

  const rows: RowDef[] = [
    { label: 'Revenue from Operations', key: 'revenue_from_operations', format: 'currency', bold: true },
    { label: 'Sale of Products', key: 'total_revenue', format: 'currency', indent: true, extKeys: ['Sale_Of_Products', 'sale_of_products'] },
    { label: 'Other Operating Revenue', key: 'total_revenue', format: 'currency', indent: true, extKeys: ['Other_Operating_Revenue', 'other_operating_revenue'] },
    { label: 'Excise Duty', key: 'total_revenue', format: 'currency', indent: true, extKeys: ['Excise_Duty', 'excise_duty'] },
    { label: 'Total Revenue', key: 'total_revenue', format: 'currency', bold: true },
    { label: '', key: 'total_revenue', format: 'currency', separator: true },
    { label: 'Direct Cost', key: 'direct_cost', format: 'currency', indent: true },
    { label: 'Employee Expenses', key: 'employee_expenses', format: 'currency', indent: true },
    { label: 'Advertising Expense', key: 'other_expenses', format: 'currency', indent: true, extKeys: ['Advertising_Expense', 'advertising_expense'] },
    { label: 'Depreciation & Amortisation', key: 'depreciation_amortisation', format: 'currency', indent: true },
    { label: 'Finance Costs', key: 'finance_costs', format: 'currency', indent: true },
    { label: 'Other Expenses', key: 'other_expenses', format: 'currency', indent: true },
    { label: 'Total Expenses', key: 'total_expenses', format: 'currency', bold: true },
    { label: '', key: 'total_revenue', format: 'currency', separator: true },
    { label: 'Gross Profit', key: 'gross_profit', format: 'currency', bold: true },
    { label: 'EBITDA', key: 'ebitda', format: 'currency', bold: true },
    { label: 'EBITDA Margin', key: 'ebitda_margin', format: 'pct', indent: true },
    { label: 'EBIT', key: 'ebit', format: 'currency', bold: true },
    { label: 'Profit Before JV & Tax', key: 'pbt', format: 'currency', indent: true, extKeys: ['Profit_Before_JV_And_Tax', 'profit_before_jv_and_tax'] },
    { label: 'Share of JV Profit / Loss', key: 'pbt', format: 'currency', indent: true, extKeys: ['Share_Of_JV_Profit_Loss', 'share_of_jv_profit_loss'] },
    { label: 'PBT (Profit Before Tax)', key: 'pbt', format: 'currency' },
    { label: 'Net Profit / PAT', key: 'net_profit', format: 'currency', bold: true },
    { label: 'EPS', key: 'eps', format: 'number' },
  ];

  const valueOf = (d: UnifiedFinancialRecord, row: RowDef): number | null => {
    if (row.extKeys) return resolveRecordValue(d, ...row.extKeys);
    const v = d[row.key];
    return typeof v === 'number' && !isNaN(v) ? v : null;
  };

  const fmt = (val: number | null | undefined, format: string) => {
    if (val == null || isNaN(val as number)) return '—';
    if (format === 'currency') return formatCompactCurrency(val as number, region);
    if (format === 'pct') return formatPercent(val as number);
    if (format === 'number') return (val as number).toFixed(2);
    return String(val);
  };

  return (
    <Card>
      <CardHeader className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-neutral-800">
          Profit & Loss Statement
        </h3>
        {unitLabel && (
          <span className="text-[10px] font-medium uppercase tracking-wider text-neutral-500 bg-neutral-100 border border-neutral-200 rounded px-2 py-1 whitespace-nowrap">
            {unitLabel}
          </span>
        )}
      </CardHeader>
      <CardBody className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b-2 border-neutral-200 bg-neutral-50">
                <th className="text-left py-2.5 px-3 text-neutral-500 font-semibold text-[11px] uppercase tracking-wider min-w-[12rem] sticky left-0 bg-neutral-50 z-10">
                  Line Item
                </th>
                {years.map((d) => (
                  <th
                    key={d.year}
                    className="text-right py-2.5 px-3 text-neutral-700 font-bold text-[11px] whitespace-nowrap min-w-[5rem]"
                  >
                    {formatFiscalYear(d.year, region)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                if (row.separator) {
                  return (
                    <tr key={idx}>
                      <td colSpan={years.length + 1} className="py-0.5 bg-neutral-100/70" />
                    </tr>
                  );
                }
                const hasAnyValue = years.some((d) => valueOf(d, row) != null);
                if (!hasAnyValue) return null;
                const rowBg = row.bold ? 'bg-neutral-50/80' : idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/30';
                const labelBg = row.bold ? 'bg-neutral-50/95' : idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/60';
                return (
                  <tr
                    key={idx}
                    className={`border-b border-neutral-100 transition-colors hover:bg-violet-50/40 ${rowBg}`}
                  >
                    <td
                      className={`py-2 px-3 text-xs leading-snug sticky left-0 z-10 ${labelBg} ${
                        row.bold ? 'font-semibold text-neutral-800' : 'text-neutral-600'
                      } ${row.indent ? 'pl-7' : ''}`}
                    >
                      {row.label}
                    </td>
                    {years.map((d) => {
                      const val = valueOf(d, row);
                      const formatted = fmt(val, row.format);
                      const isNeg = typeof val === 'number' && val < 0;
                      return (
                        <td
                          key={d.year}
                          className={`py-2 px-3 text-right tabular-nums whitespace-nowrap ${
                            isNeg ? 'text-red-600' : row.bold ? 'font-semibold text-neutral-900' : 'text-neutral-700'
                          } ${row.bold ? 'font-semibold' : ''}`}
                        >
                          {formatted}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}
